import React, { useState, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, 
  faStore, 
  faChartSimple, 
  faDollarSign, 
  faBagShopping,
  faHeart,
  faHandshake,
  faCog,
  faPalette,
  faPlus,
  faChevronRight,
  faChevronLeft,
  faEllipsisH
} from '@fortawesome/free-solid-svg-icons';
import { useStore } from "../../hooks/storeContext";
import { ROUTE_PATH, ROUTE_PRODUCT_PATH } from "../../constants/routePath";
import StorePreview from '../../pages/StorePreview';
import { Avatar, Tooltip, Modal, Dropdown } from 'antd';
import SparkleLogo from '../../assets/images/sparklelogo.png';
import { 
  SidebarContainer,
  SidebarHeader,
  LogoSection,
  UserSection,
  UserAvatar,
  UserInfo,
  NavSection,
  NavItem,
  NavIcon,
  NavLabel,
  NavDivider,
  CollapseButton,
  MobileOverlay,
  FooterSection
} from './styled';

const LeftSidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isStorePageVisible, setIsStorePageVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isShortScreen, setIsShortScreen] = useState(window.innerHeight < 800);
  const [showMorePopup, setShowMorePopup] = useState(false);
  const { authStore } = useStore();
  
  const location = useLocation();
  const currentPath = location.pathname;

  // Navigation items configuration - conditional based on screen size
  const baseNavigationItems = [
    {
      key: 'mypage',
      icon: faStore,
      label: 'My Page',
      path: ROUTE_PATH.MYSTORE,
      isActive: [
        ROUTE_PATH.MYSTORE,
        ROUTE_PATH.USERPROFILE, 
        ROUTE_PRODUCT_PATH.URL_PRODUCT, 
        ROUTE_PRODUCT_PATH.TIP_PRODUCT,
        ROUTE_PRODUCT_PATH.CUSTOM_PRODUCT,
        ROUTE_PRODUCT_PATH.DIGITAL_PRODUCT, 
        ROUTE_PRODUCT_PATH.COACHING_CALL, 
        ROUTE_PRODUCT_PATH.EMAIL_PRODUCT,
        ROUTE_PRODUCT_PATH.VIDEO_PRODUCT, 
        ROUTE_PRODUCT_PATH.EDIT_EMAIL_PRODUCT,
        ROUTE_PRODUCT_PATH.EDIT_URL_PRODUCT,
        ROUTE_PRODUCT_PATH.EDIT_VIDEO_PRODUCT,
        ROUTE_PRODUCT_PATH.EDIT_REFERRAL_PRODUCT,
        ROUTE_PATH.REFERRAL_PRODUCT
      ].includes(currentPath)
    },
    {
      key: 'design',
      icon: faPalette,
      label: 'Design',
      path: ROUTE_PATH.DESIGN,
      isActive: currentPath === ROUTE_PATH.DESIGN
    },
    {
      key: 'addlinks',
      icon: faPlus,
      label: 'Add Links',
      path: ROUTE_PATH.PRODUCTS,
      isActive: currentPath === ROUTE_PATH.PRODUCTS
    },
    { key: 'divider1', type: 'divider' },
    {
      key: 'dashboard',
      icon: faChartSimple,
      label: 'Dashboard',
      path: ROUTE_PATH.DASHBOARD,
      isActive: currentPath === ROUTE_PATH.DASHBOARD
    },
    {
      key: 'shop',
      icon: faBagShopping,
      label: 'Shop',
      path: ROUTE_PATH.CREATOR_STORE,
      isActive: currentPath === ROUTE_PATH.CREATOR_STORE || 
                currentPath === ROUTE_PATH.CREATOR_STORE_CATEGORIES ||
                (currentPath.includes('/creator-store'))
    },
    {
      key: 'income',
      icon: faDollarSign,
      label: 'Income',
      path: ROUTE_PATH.INCOME,
      isActive: currentPath === ROUTE_PATH.INCOME
    }
  ];

  // Add Preview button only if not on mobile with short screen
  const navigationItems = [
    ...baseNavigationItems,
    ...(!(isMobile && isShortScreen) ? [
      { key: 'divider2', type: 'divider' },
      {
        key: 'preview',
        icon: faEye,
        label: 'Preview',
        action: () => setIsStorePageVisible(true),
        tooltip: 'Preview your store'
      }
    ] : [])
  ];

  const moreItems = [
    {
      key: 'customers',
      icon: faHeart,
      label: 'Customers',
      path: ROUTE_PATH.CUSTOMERS
    },
    {
      key: 'payments',
      icon: faDollarSign,
      label: 'Payments',
      path: ROUTE_PATH.PAYMENTS
    },
    {
      key: 'referrals',
      icon: faHandshake,
      label: 'Referrals',
      path: ROUTE_PATH.REFERRALS
    },
    {
      key: 'settings',
      icon: faCog,
      label: 'Settings',
      path: ROUTE_PATH.PROFILE
    }
  ];

  const handleCloseStorePage = useCallback(() => {
    setIsStorePageVisible(false);
  }, []);

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    
    // Emit event for layout to listen to
    window.dispatchEvent(new CustomEvent('sidebarToggle', {
      detail: { collapsed: newCollapsedState }
    }));
  };

  const toggleMobileSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileOpen(false);
  };

  // Close mobile sidebar when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [currentPath]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      const shortScreen = window.innerHeight < 800;
      setIsMobile(mobile);
      setIsShortScreen(shortScreen);
      
      // Close mobile sidebar when switching to desktop
      if (!mobile && isMobileOpen) {
        setIsMobileOpen(false);
      }
      
      // Close more popup when switching screen sizes
      if (showMorePopup) {
        setShowMorePopup(false);
      }
    };

    const handleMobileToggle = () => {
      toggleMobileSidebar();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('toggleMobileSidebar', handleMobileToggle);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('toggleMobileSidebar', handleMobileToggle);
    };
  }, [isMobileOpen, showMorePopup]);

  // Handle mobile sidebar close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileOpen && !event.target.closest('.left-sidebar')) {
        setIsMobileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOpen]);

  const renderNavItem = (item) => {
    if (item.type === 'divider') {
      return <NavDivider key={item.key} />;
    }

    if (item.action) {
      return (
        <Tooltip key={item.key} title={isCollapsed ? item.tooltip : ''} placement="right">
          <NavItem isActive={item.isActive} isCollapsed={isCollapsed} onClick={item.action}>
            <NavIcon isCollapsed={isCollapsed}>
              <FontAwesomeIcon icon={item.icon} />
            </NavIcon>
            {!isCollapsed && <NavLabel>{item.label}</NavLabel>}
          </NavItem>
        </Tooltip>
      );
    }

    return (
      <Link key={item.key} to={item.path} onClick={closeMobileSidebar}>
        <NavItem isActive={item.isActive} isCollapsed={isCollapsed}>
          <NavIcon isCollapsed={isCollapsed}>
            <FontAwesomeIcon icon={item.icon} />
          </NavIcon>
          {!isCollapsed && <NavLabel>{item.label}</NavLabel>}
        </NavItem>
      </Link>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && <MobileOverlay onClick={closeMobileSidebar} />}

      {/* Sidebar */}
      <SidebarContainer 
        className="left-sidebar"
        isCollapsed={isCollapsed} 
        isMobileOpen={isMobileOpen}
      >
        {/* Header */}
        <SidebarHeader>
          <LogoSection isCollapsed={isCollapsed}>
            {!isCollapsed && (
              <img 
                src={SparkleLogo} 
                alt="Sparkle" 
                style={{ height: '32px' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
          </LogoSection>
          
          {/* Collapse Button - positioned to the far right */}
          <CollapseButton onClick={toggleSidebar}>
            <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
          </CollapseButton>
        </SidebarHeader>

        {/* Main Navigation */}
        <NavSection>
          {navigationItems.map(renderNavItem)}
        </NavSection>

        {/* Footer with More Section and User Section */}
        <FooterSection>
          <NavDivider />
          
          {/* Preview Button (moved up for better mobile access) */}
          {isMobile && isShortScreen && (
            <div style={{ marginBottom: '16px' }}>
              <Tooltip title={isCollapsed ? 'Preview your store' : ''} placement="right">
                <NavItem isActive={false} isCollapsed={isCollapsed} onClick={() => setIsStorePageVisible(true)}>
                  <NavIcon isCollapsed={isCollapsed}>
                    <FontAwesomeIcon icon={faEye} />
                  </NavIcon>
                  {!isCollapsed && <NavLabel>Preview</NavLabel>}
                </NavItem>
              </Tooltip>
            </div>
          )}
          
          {/* More Section - Conditional based on screen height */}
          <div style={{ marginBottom: '16px' }}>
            {!isCollapsed && (
              <div style={{ 
                padding: '0 16px', 
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '8px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                More
              </div>
            )}
            
            {/* Show only Settings on short mobile screens */}
            {isMobile && isShortScreen ? (
              <>
                {/* Settings item - always visible */}
                <Link to={ROUTE_PATH.PROFILE} onClick={closeMobileSidebar}>
                  <NavItem isActive={currentPath.includes('/settings')} isCollapsed={isCollapsed}>
                    <NavIcon isCollapsed={isCollapsed}>
                      <FontAwesomeIcon icon={faCog} />
                    </NavIcon>
                    {!isCollapsed && <NavLabel>Settings</NavLabel>}
                  </NavItem>
                </Link>
                
                {/* More options trigger */}
                {!isCollapsed && (
                  <NavItem 
                    isActive={false} 
                    isCollapsed={isCollapsed}
                    onClick={() => setShowMorePopup(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    <NavIcon isCollapsed={isCollapsed}>
                      <FontAwesomeIcon icon={faEllipsisH} />
                    </NavIcon>
                    <NavLabel>More Options</NavLabel>
                  </NavItem>
                )}
              </>
            ) : (
              /* Show all items on desktop or taller mobile screens */
              moreItems.map(item => (
                <Link key={item.key} to={item.path} onClick={closeMobileSidebar}>
                  <NavItem isActive={item.path === currentPath} isCollapsed={isCollapsed}>
                    <NavIcon isCollapsed={isCollapsed}>
                      <FontAwesomeIcon icon={item.icon} />
                    </NavIcon>
                    {!isCollapsed && <NavLabel>{item.label}</NavLabel>}
                  </NavItem>
                </Link>
              ))
            )}
          </div>

          {/* User Section */}
          <Dropdown
            menu={{
              items: [
                {
                  key: 'logout',
                  label: 'Logout',
                  onClick: () => {
                    authStore.logout();
                  },
                  style: { color: '#ff4d4f' }
                }
              ]
            }}
            placement="topLeft"
            trigger={['click']}
          >
            <NavItem 
              isActive={false} 
              isCollapsed={isCollapsed}
              style={{ 
                cursor: 'pointer',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                margin: '4px 8px',
                padding: !isCollapsed ? '12px 16px' : '6px',
                justifyContent: !isCollapsed ? 'space-between' : 'center'
              }}
            >
              {!isCollapsed ? (
                <>
                  <UserSection style={{ width: 'auto', padding: '0', border: 'none' }}>
                    <UserAvatar>
                      <Avatar
                        size={36}
                        src={authStore.me?.avatar || null}
                        style={{
                          background: authStore.me?.avatar ? 'none' : '#000',
                          color: 'white',
                        }}
                      >
                        {authStore.me?.avatar ? null : authStore.me?.userName?.charAt(0).toUpperCase()}
                      </Avatar>
                    </UserAvatar>
                    <UserInfo>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>
                        {authStore.me?.userName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        {authStore.me?.email}
                      </div>
                    </UserInfo>
                  </UserSection>
                  <NavIcon>
                    <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '12px' }} />
                  </NavIcon>
                </>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%'
                }}>
                  <Avatar
                    size={35}
                    src={authStore.me?.avatar || null}
                    style={{
                      background: authStore.me?.avatar ? 'none' : '#000',
                      color: 'white',
                    }}
                  >
                    {authStore.me?.avatar ? null : authStore.me?.userName?.charAt(0).toUpperCase()}
                  </Avatar>
                </div>
              )}
            </NavItem>
          </Dropdown>
        </FooterSection>
      </SidebarContainer>

      {/* Store Preview Modal */}
      <StorePreview
        onClose={handleCloseStorePage}
        isVisible={isStorePageVisible}
      />

      {/* More Options Popup for Short Screens */}
      <Modal
        title="More Options"
        open={showMorePopup}
        onCancel={() => setShowMorePopup(false)}
        footer={null}
        width={300}
      >
        <div style={{ padding: '8px 0' }}>
          {moreItems.filter(item => item.key !== 'settings').map(item => (
            <Link 
              key={item.key} 
              to={item.path} 
              onClick={() => {
                setShowMorePopup(false);
                closeMobileSidebar();
              }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: item.path === currentPath ? '#f0f9ff' : 'transparent',
                color: item.path === currentPath ? '#0066cc' : '#333',
                margin: '4px 0'
              }}>
                <div style={{ marginRight: '12px', width: '20px', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div>{item.label}</div>
              </div>
            </Link>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default LeftSidebar;
